import { memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Scroll, Stack } from '@/components/layout';
import { EventDetailActions } from '@/features/guardian/components/event-detail/event-detail-actions';
import {
  EventDetailHeader,
  type EventDetailSurface,
} from '@/features/guardian/components/event-detail/event-detail-header';
import { EventDetailInfoList } from '@/features/guardian/components/event-detail/event-detail-info-list';
import { EventDetailSummaryCard } from '@/features/guardian/components/event-detail/event-detail-summary-card';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { getEventDetailLabels } from '@/features/guardian/hooks/use-event-detail';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export type EventDetailContentProps = {
  event: FamilyEvent;
  surface: EventDetailSurface;
  onClose: () => void;
  onOpenMap?: (event: FamilyEvent) => void;
  onShare?: (event: FamilyEvent) => void;
  onReschedule?: (event: FamilyEvent) => void;
  onViewBooking?: (event: FamilyEvent) => void;
};

function EventDetailContentComponent({
  event,
  surface,
  onClose,
  onOpenMap,
  onShare,
  onReschedule,
  onViewBooking,
}: EventDetailContentProps) {
  const { scale } = useScaling();
  const { bottom } = useSafeAreaInsets();
  const styles = useStyles();
  const { dateLabel, bookingMetaLabel } = useMemo(() => getEventDetailLabels(event), [event]);
  const eventRef = useRef(event);

  useLayoutEffect(() => {
    eventRef.current = event;
  }, [event]);

  const handleOpenMap = useCallback(() => onOpenMap?.(event), [event, onOpenMap]);
  const handleShare = useCallback(() => onShare?.(eventRef.current), [onShare]);
  const handleReschedule = useCallback(() => onReschedule?.(event), [event, onReschedule]);
  const handleViewBooking = useCallback(() => onViewBooking?.(event), [event, onViewBooking]);
  const actions = (
    <EventDetailActions
      event={event}
      onReschedule={handleReschedule}
      onViewBooking={handleViewBooking}
    />
  );

  return (
    <Stack
      flex={1}
      minHeight={0}
      style={[styles.root, surface === 'screen' ? null : styles.roundedSurface]}>
      <EventDetailHeader
        title={event.title}
        surface={surface}
        onClose={onClose}
        onShare={handleShare}
      />
      <Scroll
        key={surface === 'panel' ? event.id : undefined}
        style={styles.scroll}
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}>
        <EventDetailSummaryCard event={event} />
        <EventDetailInfoList
          event={event}
          dateLabel={dateLabel}
          bookingMetaLabel={bookingMetaLabel}
          onOpenMap={handleOpenMap}
        />
        {surface === 'screen' ? null : <Stack style={styles.inlineActions}>{actions}</Stack>}
      </Scroll>
      {surface === 'screen' ? (
        <Stack style={[styles.actions, { paddingBottom: bottom + scale(12) }]}>{actions}</Stack>
      ) : null}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    backgroundColor: guardianColors['1'],
  },
  roundedSurface: {
    borderRadius: scale(16),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    paddingHorizontal: scale({ phone: 20, tablet: 12 }),
    paddingTop: scale(8),
    paddingBottom: scale(12),
    gap: scale(10),
  },
  inlineActions: {
    paddingTop: scale(2),
  },
  actions: {
    paddingTop: scale(8),
    paddingHorizontal: scale({ phone: 20, tablet: 12 }),
    backgroundColor: guardianColors['1'],
  },
}));

export const EventDetailContent = memo(EventDetailContentComponent);
